import { useCallback, useEffect, useRef, useState } from 'react';
import FlowkitDashboardTab from './FlowkitDashboardTab';
import CharacterFilmTabs from './CharacterFilmTabs';
import CharacterFilmStory from './CharacterFilmStory';
import CharacterFilmPipeline from './CharacterFilmPipeline';
import CharacterFilmRules from './CharacterFilmRules';
import CharacterFilmConnections from './CharacterFilmConnections';
import ScreenplayFilmPanel from './ScreenplayFilmPanel';
import {
  filmsApi, creationAttempt, filmError, filmIsActive, filmIsBusy, filmMutationObserved, PIPPIP_EXAMPLE,
  type FilmCreationAttempt, type FilmDraft, type FilmPolicyResponse, type FilmRecord,
  type FilmSummary, type FilmUiError,
} from '../services/films';
import './CharacterFilmPanel.css';

export interface CharacterFilmPanelProps { onBackToUgc?: () => void }
export type CharacterFilmAction = (label: string,
  work: (latest: FilmRecord, signal: AbortSignal) => Promise<FilmRecord>) => Promise<FilmRecord | undefined>;
type WorkspaceTab = 'story' | 'pipeline' | 'rules' | 'dashboard';
type PendingAction = { id: string; label: string; controller: AbortController };
const EMPTY_DRAFT: FilmDraft = { title: '', brief: '', episodeCount: 1, episodeSeconds: 40 };

export default function CharacterFilmPanel({ onBackToUgc }: CharacterFilmPanelProps = {}) {
  const [mode, setMode] = useState('screenplay');
  const [unsaved, setUnsaved] = useState(false);
  return <div className="character-film-modes">
    <div className="character-film screenplay-mode-switch"><CharacterFilmTabs label="Film mode" selected={mode}
      items={[{ id: 'screenplay', label: 'Screenplay' }, { id: 'manual', label: 'Manual films' }]}
      onSelect={next => {
        if (next === mode || (mode === 'screenplay' && unsaved && !window.confirm('Leave unsaved screenplay work?'))) return;
        setMode(next); setUnsaved(false);
      }} /></div>
    {mode === 'screenplay' ? <ScreenplayFilmPanel onBackToUgc={onBackToUgc} onUnsavedChange={setUnsaved} />
      : <ManualCharacterFilmPanel onBackToUgc={onBackToUgc} />}
  </div>;
}

function ManualCharacterFilmPanel({ onBackToUgc }: CharacterFilmPanelProps = {}) {
  const [films, setFilms] = useState<FilmSummary[]>([]);
  const [policy, setPolicy] = useState<FilmPolicyResponse | null>(null);
  const [policyError, setPolicyError] = useState('');
  const [listError, setListError] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, FilmRecord>>({});
  const [readErrors, setReadErrors] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, FilmUiError | undefined>>({});
  const [uncertain, setUncertain] = useState<Record<string, boolean>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [view, setView] = useState<WorkspaceTab>('story');
  const [draft, setDraft] = useState<FilmDraft>({ ...EMPTY_DRAFT });
  const [attempt, setAttempt] = useState<FilmCreationAttempt>();
  const [draftError, setDraftError] = useState('');
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const mounted = useRef(false);
  const selection = useRef<{ id: string | null; epoch: number }>({ id: null, epoch: 0 });
  const working = useRef<PendingAction | null>(null);
  const reads = useRef(new Map<string, number>());
  const uncertainBefore = useRef(new Map<string, FilmRecord>());
  const readControllers = useRef(new Set<AbortController>());
  const listController = useRef<AbortController | undefined>(undefined);
  const record = selectedId ? snapshots[selectedId] : undefined;

  const saveSnapshot = useCallback((id: string, value: FilmRecord) => {
    if (value.id !== id || !Array.isArray(value.episodes) || typeof value.title !== 'string') {
      throw new Error('The saved film response does not match the requested film. No action was submitted.');
    }
    if (!mounted.current) return;
    const before = uncertainBefore.current.get(id);
    if (before && filmMutationObserved(before, value)) {
      uncertainBefore.current.delete(id);
      setUncertain(current => ({ ...current, [id]: false }));
      setErrors(current => ({ ...current, [id]: undefined }));
    }
    setSnapshots(current => ({ ...current, [id]: value }));
    setFilms(current => {
      const summary: FilmSummary = { id: value.id, title: value.title, brief: value.brief, episodeCount: value.episodeCount,
        episodeSeconds: value.episodeSeconds, status: value.status, blockingReason: value.blockingReason,
        projectId: value.projectId, createdAt: value.createdAt, updatedAt: value.updatedAt };
      return [summary, ...current.filter(f => f.id !== id)];
    });
    setReadErrors(current => ({ ...current, [id]: '' }));
  }, []);

  const refreshList = useCallback(async () => {
    listController.current?.abort();
    const controller = new AbortController(); listController.current = controller;
    setListLoading(true);
    try {
      const result = await filmsApi.list(controller.signal);
      if (!Array.isArray(result.films)) throw new Error('Film list could not be read.');
      if (mounted.current && !controller.signal.aborted) { setFilms(result.films); setListError(''); }
    } catch (error) {
      if (mounted.current && !controller.signal.aborted) setListError(filmError(error).message);
    } finally {
      if (mounted.current && !controller.signal.aborted) setListLoading(false);
    }
  }, []);

  const refreshRecord = useCallback(async (id: string, signal?: AbortSignal, epoch?: number) => {
    const sequence = (reads.current.get(id) ?? 0) + 1; reads.current.set(id, sequence);
    const current = () => mounted.current && !signal?.aborted && reads.current.get(id) === sequence
      && (epoch === undefined || (selection.current.id === id && selection.current.epoch === epoch));
    try {
      const result = await filmsApi.get(id, signal);
      if (current()) saveSnapshot(id, result);
      return result;
    } catch (error) {
      if (current()) setReadErrors(previous => ({ ...previous, [id]: filmError(error).message }));
      throw error;
    }
  }, [saveSnapshot]);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    void refreshList();
    void filmsApi.policy(controller.signal).then(value => {
      if (!controller.signal.aborted) setPolicy(value);
    }).catch(error => { if (!controller.signal.aborted) setPolicyError(filmError(error).message); });
    return () => {
      mounted.current = false; controller.abort(); listController.current?.abort();
      readControllers.current.forEach(c => c.abort());
      // A disconnect does not cancel the server's durable intent; a later mount only GETs it.
      working.current?.controller.abort();
    };
  }, [refreshList]);

  function selectFilm(id: string | null) {
    // Re-selecting the same film does not remount effects: keep their epoch valid.
    if (selection.current.id === id) { setView('story'); return; }
    selection.current = { id, epoch: selection.current.epoch + 1 };
    setSelectedId(id); setView('story');
  }

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController(); readControllers.current.add(controller);
    const epoch = selection.current.epoch;
    setLoadingId(selectedId);
    void refreshRecord(selectedId, controller.signal, epoch).catch(() => { /* Visible read-only error; no mutation fallback. */ })
      .finally(() => { if (!controller.signal.aborted && mounted.current) setLoadingId(current => current === selectedId ? null : current); });
    return () => { controller.abort(); readControllers.current.delete(controller); };
  }, [selectedId, refreshRecord]);

  const poll = !!selectedId && (filmIsActive(record) || pending?.id === selectedId || uncertain[selectedId]);
  useEffect(() => {
    if (!selectedId || !poll) return;
    const controller = new AbortController(); readControllers.current.add(controller);
    const epoch = selection.current.epoch;
    let inFlight = false;
    const timer = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void refreshRecord(selectedId, controller.signal, epoch).catch(() => { /* Keep last snapshot and surface durable blockers. */ })
        .finally(() => { inFlight = false; });
    }, 5000);
    return () => { window.clearInterval(timer); controller.abort(); readControllers.current.delete(controller); };
  }, [selectedId, poll, refreshRecord]);

  function begin(id: string, label: string): PendingAction | undefined {
    if (working.current) return;
    const operation = { id, label, controller: new AbortController() };
    working.current = operation; setPending(operation);
    // Older observation requests must not overwrite the action's authoritative result.
    reads.current.set(id, (reads.current.get(id) ?? 0) + 1);
    return operation;
  }
  function end(operation: PendingAction) {
    if (working.current === operation) working.current = null;
    if (mounted.current) setPending(current => current === operation ? null : current);
  }

  const runAction: CharacterFilmAction = async (label, work) => {
    const id = selection.current.id;
    if (!id || uncertain[id]) return;
    const operation = begin(id, label); if (!operation) return;
    const signal = operation.controller.signal;
    let submitted = false;
    let before: FilmRecord | undefined;
    try {
      setErrors(current => ({ ...current, [id]: undefined }));
      const latest = await refreshRecord(id, signal);
      if (signal.aborted) return;
      if (filmIsBusy(latest)) throw new Error('Inspect native dashboard; do not resubmit unknown requests. A saved operation is already active or uncertain.');
      // work receives the captured film, never a newly selected film's state.
      before = latest;
      submitted = true;
      const result = await work(latest, signal);
      if (signal.aborted) return;
      reads.current.set(id, (reads.current.get(id) ?? 0) + 1); saveSnapshot(id, result);
      try { return await refreshRecord(id, signal); }
      catch { return result; } // Retain the POST snapshot, but disable actions until a successful GET.
    } catch (error) {
      if (signal.aborted || !mounted.current) return;
      const detail = filmError(error);
      setErrors(current => ({ ...current, [id]: detail }));
      if (submitted && detail.uncertain && before) {
        uncertainBefore.current.set(id, before);
        setUncertain(current => ({ ...current, [id]: true }));
      }
      try { await refreshRecord(id, signal); }
      catch { /* Last snapshot and local uncertainty stay visible. Never retry POST. */ }
    } finally {
      end(operation);
      if (mounted.current) void refreshList();
    }
  };

  async function createDraft() {
    if (working.current) return;
    let intent: FilmCreationAttempt;
    try { intent = creationAttempt(draft, attempt); setAttempt(intent); setDraftError(''); }
    catch (error) { setDraftError(filmError(error).message); return; }
    const id = intent.input.id, epoch = selection.current.epoch;
    const operation = begin(id, 'Creating draft'); if (!operation) return;
    const signal = operation.controller.signal;
    const finish = (result: FilmRecord) => {
      if (!mounted.current || signal.aborted) return;
      saveSnapshot(id, result); setAttempt(undefined); setDraft({ ...EMPTY_DRAFT }); setSampleLoaded(false); setDraftError('');
      if (selection.current.epoch === epoch) selectFilm(id);
    };
    try {
      // Creating a local draft is idempotent by this immutable UUID/payload.
      const result = await filmsApi.create(intent.input, signal); finish(result);
    } catch (error) {
      if (!mounted.current || signal.aborted) return;
      try { finish(await filmsApi.get(id, signal)); }
      catch { if (mounted.current && !signal.aborted) setDraftError(filmError(error).message); }
    } finally { end(operation); if (mounted.current) void refreshList(); }
  }

  function manualRefresh() {
    if (!selectedId) return;
    const id = selectedId, controller = new AbortController(); readControllers.current.add(controller);
    setLoadingId(id);
    void refreshRecord(id, controller.signal, selection.current.epoch).catch(() => { /* Retain snapshot. */ })
      .finally(() => {
        readControllers.current.delete(controller);
        if (mounted.current) setLoadingId(current => current === id ? null : current);
      });
  }

  const disabled = !!pending || !!(selectedId && (readErrors[selectedId] || uncertain[selectedId])) || filmIsBusy(record);
  const reconcile = !!record && (!!record.operation || record.status === 'reconciliation_required' || uncertain[record.id]);
  return <section className="character-film" aria-label="Character Films workspace">
    <header className="character-film-header">
      <div><p className="character-film-eyebrow">STUDIO / CHARACTER MODE</p><h1>Character Films</h1>
        <p>One project per film · 32 or 40 seconds per episode · OpenCut-only export</p></div>
      {onBackToUgc && <button type="button" onClick={onBackToUgc}>Back to UGC</button>}
    </header>
    <CharacterFilmConnections />
    {pending && <div className="character-film-notice" role="status" aria-live="polite">
      <strong>{pending.label}…</strong> Film <code>{pending.id}</code>. Planning may take several minutes; provisioning and export can take up to 11 minutes.
      Switching views does not change the request target. Do not resubmit.
    </div>}
    <div className="character-film-layout">
      <aside className="character-film-sidebar" aria-label="Films and new draft">
        <div className="character-film-section-heading"><h2>Film library</h2>
          <button type="button" onClick={() => void refreshList()} disabled={listLoading} aria-label="Refresh film list">Refresh</button></div>
        {listError && <p className="character-film-error" role="alert">{listError}</p>}
        {listLoading && !films.length && <p role="status">Loading saved films…</p>}
        {!listLoading && !films.length && <p className="character-film-muted">No films yet. Start with a brief; nothing generates on load.</p>}
        <ul className="character-film-library">{films.map(film => <li key={film.id}>
          <button type="button" aria-label={`Select film ${film.title}`} aria-pressed={selectedId === film.id}
            onClick={() => selectFilm(film.id)}><strong>{film.title}</strong>
            <span>{film.episodeCount} ep · {film.episodeSeconds}s <span className="character-film-badge">{film.status.replace(/_/g, ' ')}</span></span>
            {film.blockingReason && <span className="character-film-error">{film.blockingReason.code.replace(/_/g, ' ')}</span>}
          </button></li>)}</ul>
        <section className="character-film-draft" aria-label="New film draft">
          <h2>New film</h2>
          <form onSubmit={event => { event.preventDefault(); void createDraft(); }}>
            <fieldset disabled={!!pending || !!attempt}>
              <label>Film title<input value={draft.title} maxLength={120} required onChange={event => setDraft(current => ({ ...current, title: event.target.value }))} /></label>
              <label>Film brief<textarea aria-label="Film brief" rows={7} value={draft.brief} maxLength={8000} required onChange={event => setDraft(current => ({ ...current, brief: event.target.value }))} /></label>
              <div className="character-film-form-row">
                <label>Episode count<input type="number" min={1} max={8} step={1} required value={draft.episodeCount}
                  onChange={event => setDraft(current => ({ ...current, episodeCount: Number(event.target.value) }))} /></label>
                <label>Episode seconds<select aria-label="Episode seconds" value={draft.episodeSeconds} onChange={event => setDraft(current => ({ ...current, episodeSeconds: Number(event.target.value) as 32 | 40 }))}>
                  <option value={32}>32s · 4 clips</option><option value={40}>40s · 5 clips</option></select></label>
              </div>
              <button type="button" onClick={() => { setDraft({ ...PIPPIP_EXAMPLE }); setSampleLoaded(true); setDraftError(''); }}>Load Pippip example</button>
            </fieldset>
            <p className={sampleLoaded ? 'character-film-notice' : 'character-film-muted'}>
              Brief only. Replace the sample product with a real item + evidence. No source, image or approval is created by the example.
            </p>
            {attempt && <p className="character-film-meta">Creation attempt <code>{attempt.input.id}</code>. Retry keeps the same ID and draft.</p>}
            {draftError && <p className="character-film-error" role="alert">{draftError}</p>}
            <button className="character-film-primary" type="submit" disabled={!!pending}>
              {attempt && draftError ? 'Retry same draft' : pending?.label === 'Creating draft' ? 'Creating draft…' : 'Create film draft'}
            </button>
          </form>
        </section>
      </aside>
      <div className="character-film-main">
        <CharacterFilmTabs label="Film workspace" selected={view} onSelect={id => setView(id as WorkspaceTab)} items={[
          { id: 'story', label: 'Story' }, { id: 'pipeline', label: 'Film pipeline' }, { id: 'rules', label: 'Rules' },
          { id: 'dashboard', label: 'Flowkit dashboard', disabled: !record?.projectId },
        ]} />
        {selectedId && <div className="character-film-toolbar">
          <span className="character-film-muted">{poll ? 'Observing every 5 seconds · no automatic generation' : 'Saved state · explicit actions only'}</span>
          <button type="button" onClick={manualRefresh} disabled={loadingId === selectedId}>Refresh saved film</button>
        </div>}
        {selectedId && readErrors[selectedId] && <div className="character-film-error character-film-notice" role="alert">
          <strong>Saved state could not be refreshed.</strong> {readErrors[selectedId]} Showing the last snapshot, read-only. Refresh before acting.
        </div>}
        {record && <div className="character-film-record-header">
          <div><h2>{record.title}</h2><code className="character-film-meta">Film ID {record.id}</code></div>
          <dl className="character-film-metrics"><div><dt>Status</dt><dd>{record.status.replace(/_/g, ' ')}</dd></div>
            <div><dt>Episodes</dt><dd>{record.episodeCount} × {record.episodeSeconds}s</dd></div>
            <div><dt>Structure</dt><dd>{record.episodeSeconds / 8} × 8s clips</dd></div></dl>
        </div>}
        {record?.blockingReason && <div className="character-film-error character-film-notice" role="alert">
          <strong>{record.blockingReason.code.replace(/_/g, ' ')}</strong><p>{record.blockingReason.message}</p>
        </div>}
        {record && errors[record.id] && <div className="character-film-error character-film-notice" role="alert">{errors[record.id]!.message}</div>}
        {reconcile && <div className="character-film-warning character-film-notice" role="status">
          <strong>Inspect native dashboard; do not resubmit unknown requests.</strong>
          <p>The server's saved operation is authoritative. Polling only observes it; no request is replayed.</p>
          {record?.operation && <dl className="character-film-identifiers">
            <dt>Operation ID</dt><dd><code>{record.operation.id}</code></dd>
            <dt>Kind / status</dt><dd>{record.operation.kind} / {record.operation.status}</dd>
            <dt>Target</dt><dd><code>{record.operation.target}</code></dd>
          </dl>}
        </div>}
        {view === 'rules' ? <CharacterFilmRules policy={policy} error={policyError} record={record} />
          : !record ? <div className="character-film-empty" role="status">
            <h2>{selectedId ? 'Loading film' : 'Build a source-grounded series'}</h2>
            <p>{selectedId ? 'Waiting for the selected saved record. No generation will start.'
              : 'Select a film or create a local draft. Then bind its notebook, review its story, and advance one stage at a time.'}</p>
            <ol><li>Brief + dedicated notebook</li><li>Evidence + original product</li><li>Owner review + separate project</li><li>References → images → videos → OpenCut</li></ol>
          </div> : <>
            <div hidden={view !== 'story'}><CharacterFilmStory key={record.id} record={record} policy={policy} disabled={disabled} onAction={runAction} /></div>
            {view === 'pipeline' && <CharacterFilmPipeline key={record.id} record={record} policy={policy} disabled={disabled} onAction={runAction} />}
            {view === 'dashboard' && record.projectId && <div className="character-film-dashboard"><FlowkitDashboardTab key={record.projectId} projectId={record.projectId} /></div>}
          </>}
      </div>
    </div>
  </section>;
}